import React from 'react'
import { motion } from 'framer-motion'
import { Trophy, Award, Star, Calendar, MapPin } from 'lucide-react'
import { Achievement } from '../types'
import { fadeUp, staggerContainer, viewportOnce, cardHover } from '../lib/motion'

interface AchievementsProps {
  achievements: Achievement[]
}

const Achievements: React.FC<AchievementsProps> = ({ achievements }) => {
  const getPositionIcon = (position: string) => {
    if (position.includes('1st') || position.includes('Winner')) {
      return <Trophy className="h-6 w-6 text-yellow-500" />
    } else if (position.includes('2nd')) {
      return <Award className="h-6 w-6 text-gray-400" />
    } else if (position.includes('3rd')) {
      return <Award className="h-6 w-6 text-amber-600" />
    } else if (position.includes('Finalist')) {
      return <Star className="h-6 w-6 text-accent-500" />
    }
    return <Award className="h-6 w-6 text-primary-500" />
  }

  const getPositionColor = (position: string) => {
    if (position.includes('1st') || position.includes('Winner')) {
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border-yellow-200 dark:border-yellow-800'
    } else if (position.includes('2nd')) {
      return 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-800'
    } else if (position.includes('3rd')) {
      return 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-800'
    } else if (position.includes('Finalist')) {
      return 'bg-accent-100 dark:bg-accent-900/30 text-accent-800 dark:text-accent-200 border-accent-200 dark:border-accent-800'
    }
    return 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200 border-primary-200 dark:border-primary-800'
  }

  const totalAchievements = achievements?.length || 0
  const firstPlaces = achievements?.filter(a => a.position?.includes('1st') || a.position?.includes('Winner')).length || 0
  const competitions = new Set(achievements?.map(a => a.event)).size || 0
  const years = new Set(achievements?.map(a => a.year)).size || 0

  return (
    <section id="achievements" className="py-20 bg-gray-50 dark:bg-gray-800">
      <div className="container-max section-padding">
        <motion.div
          className="text-center mb-12 sm:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={fadeUp}
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Achievements & Awards
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-accent-500 mx-auto rounded-full"></div>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-4 max-w-2xl mx-auto px-4">
            Recognition and awards from various competitions and hackathons
          </p>
        </motion.div>

        {/* Achievement Stats */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-12 sm:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer}
        >
          <motion.div className="text-center" variants={fadeUp} whileHover={{ y: -3 }}>
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="text-2xl sm:text-3xl font-bold text-primary-600 dark:text-primary-400 mb-1 sm:mb-2">
                {totalAchievements}
              </div>
              <div className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm">Total Awards</div>
            </div>
          </motion.div>
          <motion.div className="text-center" variants={fadeUp} whileHover={{ y: -3 }}>
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="text-2xl sm:text-3xl font-bold text-yellow-600 dark:text-yellow-400 mb-1 sm:mb-2">
                {firstPlaces}
              </div>
              <div className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm">First Places</div>
            </div>
          </motion.div>
          <motion.div className="text-center" variants={fadeUp} whileHover={{ y: -3 }}>
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="text-2xl sm:text-3xl font-bold text-accent-600 dark:text-accent-400 mb-1 sm:mb-2">
                {competitions}
              </div>
              <div className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm">Competitions</div>
            </div>
          </motion.div>
          <motion.div className="text-center" variants={fadeUp} whileHover={{ y: -3 }}>
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="text-2xl sm:text-3xl font-bold text-green-600 dark:text-green-400 mb-1 sm:mb-2">
                {years}
              </div>
              <div className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm">Active Years</div>
            </div>
          </motion.div>
        </motion.div>

        {/* Achievements Grid */}
        <motion.div
          className="grid sm:grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-12 sm:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer}
        >
          {achievements?.map((achievement, index) => (
            <motion.div
              key={index}
              className="bg-white dark:bg-gray-900 rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border-l-4 border-primary-500"
              variants={fadeUp}
              whileHover={cardHover.whileHover}
              transition={cardHover.transition}
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 sm:mb-6">
                <div className="flex items-center mb-3 sm:mb-0">
                  <div className="p-2 sm:p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg mr-3 sm:mr-4">
                    {React.cloneElement(getPositionIcon(achievement.position), { className: "h-5 w-5 sm:h-6 sm:w-6" })}
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-1">
                      {achievement.event}
                    </h3>
                    <div className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium border ${getPositionColor(achievement.position)}`}>
                      {achievement.position}
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4 sm:mb-6 leading-relaxed">
                {achievement.description}
              </p>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs sm:text-sm text-gray-500 dark:text-gray-400 gap-2 sm:gap-0">
                <div className="flex items-center">
                  <Calendar className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                  <span>{achievement.year}</span>
                </div>
                {achievement.location && (
                  <div className="flex items-center">
                    <MapPin className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                    <span>{achievement.location}</span>
                  </div>
                )}
              </div>

              {achievement.certificate && (
                <div className="mt-3 sm:mt-4">
                  <a
                    href={achievement.certificate}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm sm:text-base text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors duration-200"
                  >
                    <Award className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                    View Certificate
                  </a>
                </div>
              )}
            </motion.div>
          ))}
        </motion.div>

        {/* Achievement Timeline */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 sm:p-8 shadow-lg">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6 sm:mb-8 text-center">
            Achievement Timeline
          </h3>
          <div className="relative">
            {/* Mobile: Left-aligned timeline */}
            <div className="block sm:hidden">
              <div className="absolute left-4 top-0 w-0.5 h-full bg-gradient-to-b from-primary-500 to-accent-500 rounded-full"></div>
              {achievements?.map((achievement, index) => (
                <div key={index} className="flex items-start mb-6 pl-10">
                  <div className="absolute left-2.5 w-3 h-3 bg-primary-500 rounded-full border-2 border-white dark:border-gray-900"></div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 w-full">
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                      {achievement.event}
                    </h4>
                    <p className="text-xs text-primary-600 dark:text-primary-400 mb-1">
                      {achievement.position}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {achievement.year}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Desktop: Center-aligned timeline */}
            <div className="hidden sm:block">
              <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-primary-500 to-accent-500 rounded-full"></div>
              {achievements?.map((achievement, index) => (
                <div key={index} className={`flex items-center mb-8 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                      <h4 className="font-bold text-gray-900 dark:text-white mb-1">
                        {achievement.event}
                      </h4>
                      <p className="text-sm text-primary-600 dark:text-primary-400 mb-2">
                        {achievement.position}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {achievement.year}
                      </p>
                    </div>
                  </div>
                  <div className="relative z-10">
                    <div className="w-4 h-4 bg-primary-500 rounded-full border-4 border-white dark:border-gray-900"></div>
                  </div>
                  <div className="w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Achievements
